import React, {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import SendIcon from "@mui/icons-material/Send";
import StopIcon from "@mui/icons-material/Stop";
import "./ChatOverlay.css";
import ReactDOM from "react-dom";
import { ChatLoader } from "./ChatLoader";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TranslateIcon from "@mui/icons-material/Translate";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import TranslateDialog from "./TranslateDialog";
import Compressor from "compressorjs";
import StatsComponent from "./StatsComponent";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import IconButton from "@mui/material/IconButton";

const ChatOverlay = ({
  text,
  image,
  timestamp,
  modelConfig,
  config,
  enablePrompts = true,
  imagePrompt = "",
  hasBottomBar,
  onImageUpload, // callback passed from parent
  usePortal = false,
  className = "",
  onLanguageChange,
  externalImageId,
}) => {
  const [messages, setMessages] = useState(
    JSON.parse(sessionStorage.getItem("chatMessages")) || []
  );
  const messagesRef = useRef(messages);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [showSuggestivePrompts, setShowSuggestivePrompts] = useState(true);
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false);
  const [language, setLanguage] = useState(modelConfig?.language || "english");

  const fileInputRef = useRef(null);
  const endOfMessagesRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [debugMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("debug") === "true";
  });

  console.log(externalImageId);

  const bottomOffset = hasBottomBar ? "60px" : "0px";
  const BASE_URL =
    modelConfig?.langGraph?.toString() === "true" ? process.env.REACT_APP_MEMORY_API_URL?.trim() : process.env.REACT_APP_API_HOST_URL?.trim() || "https://devapi.fiduciaai.in";
  const sessionId = sessionStorage.getItem("sessionId") || "default_session_id";
  const storeMessages = (messages) => {
    const last5Messages = messages.slice(-5);
    sessionStorage.setItem("chatMessages", JSON.stringify(last5Messages));
  };

  const [statsMap, setStatsMap] = useState(new Map([]));
  const addStat = useCallback((key, value) => {
    setStatsMap((prev) => {
      const updated = new Map(prev);
      updated.set(key, value);
      return updated;
    });
  }, []);

  const compressFile = (file) => {
    const isEnabled = Boolean(modelConfig?.compressionEnabled);
    const quality = parseFloat(modelConfig?.compressionQuality);

    if (!isEnabled || isNaN(quality)) {
      console.debug(
        !isEnabled
          ? "Compression flag disabled; returning original file."
          : "No valid compressionQuality set; returning original file."
      );
      return Promise.resolve(file);
    }

    return new Promise((resolve, reject) => {
      console.debug(`Original file size: ${file.size} bytes`);
      addStat("Original file size", file.size + "bytes");
      new Compressor(file, {
        quality,
        mimeType: "image/png",
        width: file.width,
        height: file.height,
        convertSize: 0,
        strict: true,
        success(resultBlob) {
          console.debug(`After compression (PNG): ${resultBlob.size} bytes`);
          addStat("After compression (PNG)", resultBlob.size + "bytes");

          resolve(resultBlob);
        },
        error(err) {
          console.error("Compression error:", err);
          reject(err);
        },
      });
    });
  };

  // useLayoutEffect(() => {
  //   if (isChatVisible) {
  //     endOfMessagesRef.current?.scrollIntoView({ behavior: "smooth" });
  //   }
  // }, [messages, isChatVisible]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (!text && !image) return;

    setMessages((prevMessages) => {
      if (
        prevMessages.some(
          (msg) => msg.question.text === text && msg.question.image === image
        )
      ) {
        return prevMessages;
      }
      const newMessage = {
        question: { text: text || "", image: image || null },
        response: "",
        isStreaming: true,
        showMore: true,
      };
      if (text) {
        fetchResponse(newMessage);
      }
      return [...prevMessages, newMessage];
    });
  }, [text, image, timestamp]);

  useEffect(() => {
    if (modelConfig?.language) {
      setLanguage(modelConfig.language);
    }
  }, [modelConfig?.language]);

  useEffect(() => {
    if (translateDialogOpen) {
      setIsChatVisible(false);
    }
  }, [translateDialogOpen]);

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    if (onLanguageChange) {
      onLanguageChange(newLanguage);
    }
  };

  const fetchResponse = async (newMessage) => {
    setIsLoading(true);
    setError(null);
    setIsChatVisible(true);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const endpoint = modelConfig?.langGraph ? "memory" : "rag";

    try {
      let response;

      if (image && newMessage.question.text === modelConfig.imagePrompt) {
        const formData = new FormData();
        formData.append("customerkey", modelConfig.customerKey);
        formData.append("ocid", modelConfig.ocid);
        formData.append(
          "query",
          newMessage.question.text !== modelConfig.imagePrompt
            ? newMessage.question.text
            : ""
        );

        formData.append(
          "default_query",
          modelConfig.defaultPrompt || "Some default query"
        );
        formData.append("html_flag", "true");
        formData.append("model", modelConfig.model);
        formData.append("threshold", modelConfig.threshold);
        formData.append("context_prompt", modelConfig.context_prompt);
        formData.append("record_size", modelConfig.record_size);
        formData.append("conversation_id", sessionId);
        formData.append("bottype", "cam");
        formData.append("image_query", modelConfig.imagePrompt);
        formData.append("chain_model", modelConfig.chainModel);
        formData.append("max_tokens", modelConfig.maxTokens);
        formData.append("language", modelConfig.language);
        if (externalImageId) {
          formData.append("external_image_id", externalImageId);
        }
        formData.append("langgraph", modelConfig?.langGraph?.toString() || "false");

        if (newMessage.question.image) {
          let blob = null;
          if (
            typeof newMessage.question.image === "string" &&
            (newMessage.question.image.startsWith("data:image/") ||
              newMessage.question.image.startsWith("blob:"))
          ) {
            const res = await fetch(newMessage.question.image);
            blob = await res.blob();
            blob = new File([blob], "query.png", {
              type: blob.type || "image/png",
            });
          } else if (newMessage.question.image instanceof File) {
            blob = newMessage.question.image;
          } else {
            blob = new File([newMessage.question.image], "query.png", {
              type: newMessage.question.image.type || "image/png",
            });
          }
          const file = new File([blob], "query.png", {
            type: blob.type || "image/png",
          });

          const compressedBlob = await compressFile(file);
          const compressedFile = new File([compressedBlob], file.name, {
            type: compressedBlob.type,
          });
          formData.append("images", compressedFile);
        }

        response = await fetch(
          BASE_URL + "/" + endpoint + "/api/v2/chat/img/stream/response",
          {
            method: "POST",
            body: formData,
            headers: { Accept: "text/event-stream" },
            signal: abortController.signal,
          }
        );
      } else {
        const payload = {
          query: newMessage.question.text || "",
          conversation_id: sessionId || "default_session_id",
          default_query: modelConfig.defaultPrompt || "Some default prompt",
          customerkey: modelConfig.customerKey,
          ocid: modelConfig.ocid,
          html_flag: false,
          model: modelConfig.model,
          threshold: modelConfig.threshold,
          context_prompt: modelConfig.context_prompt,
          record_size: modelConfig.record_size || 100,
          bottype: "cam",
          chain_model: modelConfig.chainModel,
          max_tokens: modelConfig.maxTokens,
          langgraph: modelConfig?.langGraph?.toString() || "false",
          language: modelConfig.language,
          ...(externalImageId && { external_image_id: externalImageId }),
        };

        response = await fetch(
          BASE_URL + "/" + endpoint + "/api/v2/chat/stream/response",
          {
            method: "POST",
            body: JSON.stringify(payload),
            headers: {
              Accept: "text/event-stream",
              "Content-Type": "application/json",
            },
          }
        );
      }

      const currentIndex = messagesRef.current.length - 1;
      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const extractedContent = (chunk.match(/\$(.*?)\$/gs) || [])
            .map((str) => str.slice(1, -1))
            .join("");

          if (extractedContent.includes("duration_for_embeddings")) {
            addStat(extractedContent, "");
            continue;
          }

          setMessages((prevMessages) =>
            prevMessages.map((msg, index) =>
              index === currentIndex
                ? {
                  ...msg,
                  response: (msg.response || "") + extractedContent,
                  isStreaming: true,
                }
                : msg
            )
          );
        }

        setMessages((prevMessages) =>
          prevMessages.map((msg, index) =>
            index === currentIndex ? { ...msg, isStreaming: false } : msg
          )
        );
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching response:", error);
      setError("Something went wrong while querying.");
      setIsLoading(false);
    }
  };

  const handleTryAgain = () => {
    setError(null);
  };

  const handleStopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsLoading(false);
    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.isStreaming ? { ...msg, isStreaming: false } : msg
      )
    );
  };

  const handleInputSubmit = (prompt, label, fileAttachment = null) => {
    setIsChatVisible(true);
    const messageText = fileAttachment
      ? modelConfig.imagePrompt
      : prompt || inputValue || "";
    const newMessage = {
      question: {
        text: messageText,
        image: fileAttachment ? fileAttachment : image || null,
        label: label || null,
      },
      response: "",
      isStreaming: true,
      showMore: true,
    };
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages, newMessage];
      messagesRef.current = updatedMessages;
      return updatedMessages;
    });
    fetchResponse(newMessage);
    setInputValue("");
    setShowSuggestivePrompts(false);
  };

  const speakText = (htmlString) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    const plainText = tempDiv.textContent || tempDiv.innerText || "";

    const utterance = new SpeechSynthesisUtterance(plainText);
    window.speechSynthesis.speak(utterance);
  };

  const handleAttachFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Validate file type.
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      alert("Please upload a JPEG or PNG image.");
      return;
    }
    // Use the parent's callback to update the uploaded file.
    if (onImageUpload) {
      onImageUpload(file);
    }
    // Trigger the chat flow.
    handleInputSubmit(null, null, file);
    e.target.value = null;
  };

  const toggleChatVisibility = () => {
    setIsChatVisible(!isChatVisible);
  };

  const handleTranslateClick = () => {
    setTranslateDialogOpen(true);
    setIsChatVisible(false);
  };

  const content = (
    <div
      className={`chat-overlay ${className}`}
      style={{ "--chat-bottom-offset": hasBottomBar ? "60px" : "0px" }}
    >
      {/* file input, prompt buttons, messages container, input bar, etc. */}
      <input
        type="file"
        accept="image/jpeg, image/jpg, image/png"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleAttachFileChange}
      />

      {showSuggestivePrompts && enablePrompts && (
        <div className="prompt-buttons">
          {config?.prompt1label && (
            <button
              className="prompt-button"
              onClick={() => {
                setInputValue(config?.promt1 || "");
                handleInputSubmit(config?.promt1 || "", config?.prompt1label);
              }}
            >
              {config?.prompt1label}
            </button>
          )}
          {config?.prompt2label && (
            <button
              className="prompt-button"
              onClick={() => {
                setInputValue(config?.promt2 || "");
                handleInputSubmit(config?.promt2 || "", config?.prompt2label);
              }}
            >
              {config?.prompt2label}
            </button>
          )}
        </div>
      )}
      {isChatVisible && (
        <div className="messages-container">
          {messages.map((msg, index) => (
            <div key={index} className="message">
              {msg.question && (
                <p className="user-message">
                  {msg.question?.label ||
                    (msg.question?.text !== imagePrompt
                      ? msg.question?.text
                      : "SpeedShotX Insights....")}
                </p>
              )}
              {msg.response && (
                <div className="response-container">
                  <p
                    className="response-message"
                    dangerouslySetInnerHTML={{ __html: msg.response }}
                  />
                  <IconButton
                    className="speaker-button"
                    onClick={() => speakText(msg.response)}
                    aria-label="Read response aloud"
                    size="small"
                    sx={{ color: "white" }}
                  >
                    <VolumeUpIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
            </div>
          ))}

          {error && (
            <div className="error-message">
              <p>
                {error}{" "}
                <span
                  onClick={() => setError(null)}
                  style={{ color: "red", cursor: "pointer" }}
                >
                  Try Again!
                </span>
              </p>
            </div>
          )}
          {isLoading && <ChatLoader color={"white"} />}
          <div ref={endOfMessagesRef} style={{ height: "1px" }} />
        </div>
      )}

      <div className="input-bar">
        <div className="expand-icon-container" onClick={toggleChatVisibility}>
          {isChatVisible ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </div>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="SpeedShotX Insights...."
          className="rounded-input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              isLoading ? handleStopStreaming() : handleInputSubmit();
            }
          }}
        />

        {modelConfig.attachEnabled ? (
          <button
            className="icon-button"
            onClick={() => {
              fileInputRef.current && fileInputRef.current.click();
            }}
          >
            <AttachFileIcon />
          </button>
        ) : (
          <></>
        )}
        {modelConfig.translateEnabled ? (
          <button className="icon-button" onClick={handleTranslateClick}>
            <TranslateIcon />
          </button>
        ) : (
          <></>
        )}
        <button
          onClick={() => {
            isLoading ? handleStopStreaming() : handleInputSubmit();
          }}
          className="send-button"
        >
          {isLoading ? <StopIcon /> : <SendIcon />}
        </button>
      </div>
      {debugMode && <StatsComponent data={statsMap} />}

      <TranslateDialog
        open={translateDialogOpen}
        onClose={() => setTranslateDialogOpen(false)}
        language={language}
        setLanguage={setLanguage}
        onLanguageChange={handleLanguageChange}
      />
    </div>
  );

  if (usePortal) {
    return ReactDOM.createPortal(content, document.body);
  } else {
    return content;
  }
};

export default ChatOverlay;
